import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Share2, ExternalLink, ShieldCheck, Globe } from "lucide-react";
import api from "@/lib/api";
import usePageTitle from "@/hooks/usePageTitle";

export default function Network() {
  usePageTitle("SocialPulse Network");
  const { data: sites = [] } = useQuery({
    queryKey: ["network-sites"],
    queryFn: () => api.get("/network/sites").then((r) => r.data),
  });
  const [active, setActive] = useState(null);
  const current = active || (sites.find((s) => s.count > 0)?.site ?? sites[0]?.site);

  const { data: cards = [] } = useQuery({
    queryKey: ["network", current],
    queryFn: () => api.get(`/network/${current}`).then((r) => r.data),
    enabled: !!current,
  });

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-12">
      <div className="flex items-center gap-2 text-brand-clay">
        <Share2 className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-widest">Content Distribution</span>
      </div>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl text-brand-ink tracking-tight">
        The SocialPulse Network
      </h1>
      <p className="mt-4 max-w-2xl text-brand-muted">
        Reviewed Ayurvedam.com content is shared as summary cards across sister sites. Every card links
        back to the canonical article on Ayurvedam.com — no duplicate full text, one source of truth.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {sites.map((s) => (
          <button
            key={s.site}
            data-testid={`network-site-${s.site}`}
            onClick={() => setActive(s.site)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
              current === s.site
                ? "border-brand bg-brand text-brand-sand"
                : "border-brand-line bg-white text-brand-muted hover:border-brand"
            }`}
          >
            <Globe className="h-3.5 w-3.5" /> {s.site}
            <span className={`rounded-full px-1.5 text-xs ${current === s.site ? "bg-white/20" : "bg-brand-mist"}`}>
              {s.count}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-lg border border-brand-line bg-brand-mist/50 px-4 py-2.5 text-xs text-brand-muted">
        Previewing how <span className="font-semibold text-brand-ink">{current}</span> displays distributed
        Ayurveda content. This is a network preview inside Ayurvedam.com.
      </div>

      {cards.length === 0 ? (
        <p className="mt-16 text-center text-brand-muted">No content distributed to {current} yet.</p>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.07 }}
              data-testid={`network-card-${c.slug}`}
              className="overflow-hidden rounded-xl border border-brand-line bg-white shadow-sm"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={c.feature_image} alt={c.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-5">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-clay">{c.topic}</span>
                <h3 className="mt-1.5 font-heading text-lg text-brand-ink">{c.title}</h3>
                <p className="mt-2 line-clamp-3 text-sm text-brand-muted">{c.summary}</p>
                {c.reviewer && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand">
                    <ShieldCheck className="h-3.5 w-3.5 text-brand-sage" /> Reviewed · {c.reviewer}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between border-t border-brand-line pt-3">
                  <span className="text-[11px] text-brand-muted">Source: {c.canonical_owner} · v{c.version}</span>
                  <Link
                    to={`/articles/${c.slug}`}
                    data-testid={`network-canonical-${c.slug}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:gap-1.5 transition-all"
                  >
                    Read on Ayurvedam.com <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
